import React, { useRef, useState } from 'react';
import { HelpCircle, Image as ImageIcon, RotateCcw, Loader2, Plus } from 'lucide-react';
import { useAppActions, useAppState, CanvasMode } from '../app/appContext';
import { useT } from '@/contexts/I18nContext';
import { Button } from '@/components/ui/button';
import { Tooltip } from '@/components/ui/tooltip';
import { Popover } from '@/components/ui/popover';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';

interface PolygonToolbarProps {
  onReloadFrame?: () => void;
  reloading?: boolean;
}

const TYPE_GROUPS: ReadonlyArray<ReadonlyArray<{ value: CanvasMode; tKey: string }>> = [
  [
    { value: 'create_rack',  tKey: 'zoning.types.rack'  },
    { value: 'create_shelf', tKey: 'zoning.types.shelf' },
  ],
  [
    { value: 'create_table',         tKey: 'zoning.types.table'         },
    { value: 'create_queue',         tKey: 'zoning.types.queue'         },
    { value: 'create_order_waiting', tKey: 'zoning.types.order_waiting' },
  ],
  [
    { value: 'create_cashier', tKey: 'zoning.types.cashier' },
    { value: 'create_staff',   tKey: 'zoning.types.staff'   },
  ],
  [
    { value: 'create_standalone', tKey: 'zoning.types.standalone' },
  ],
];

const PolygonToolbar: React.FC<PolygonToolbarProps> = ({ onReloadFrame, reloading = false }) => {
  const { t } = useT();
  const { setMode } = useAppActions();
  const { polygons, camera } = useAppState();
  const mode = polygons.ui.mode;
  const helpRef = useRef<HTMLButtonElement>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const isCompact = useMediaQuery('(max-width: 900px)');

  const HINTS: Array<{ section: string; items: Array<[string, string]> }> = [
    { section: t('zoning.hints.drawing'), items: [
      [t('zoning.hints.draw.pickType'), t('zoning.hints.draw.pickTypeDesc')],
      [t('zoning.hints.draw.click'),    t('zoning.hints.draw.clickDesc')],
      [t('zoning.hints.draw.repeat'),   t('zoning.hints.draw.repeatDesc')],
      [t('zoning.hints.draw.save'),     t('zoning.hints.draw.saveDesc')],
    ]},
    { section: t('zoning.hints.editing'), items: [
      [t('zoning.hints.edit.click'), t('zoning.hints.edit.clickDesc')],
      [t('zoning.hints.edit.shift'), t('zoning.hints.edit.shiftDesc')],
      [t('zoning.hints.edit.edge'),  t('zoning.hints.edit.edgeDesc')],
      [t('zoning.hints.edit.box'),   t('zoning.hints.edit.boxDesc')],
      [t('zoning.hints.edit.del'),   t('zoning.hints.edit.delDesc')],
      [t('zoning.hints.edit.undo'),  t('zoning.hints.edit.undoDesc')],
    ]},
    { section: t('zoning.hints.general'), items: [
      ['Esc', t('zoning.hints.escDesc')],
    ]},
  ];

  const canReload = Boolean(
    camera.selectedCameraId &&
    camera.selectedCamera?.active &&
    camera.selectedCamera?.online
  );

  const handleMode = (next: string | null) => {
    setMode((next ?? 'view') as CanvasMode);
  };

  const handlePickFromAddMenu = (next: CanvasMode) => {
    setAddMenuOpen(false);
    setMode(next);
  };

  return (
    <div className="flex w-full items-center gap-3">
      {/* Type toggle groups — full set on wide screens, single "+" dropdown on compact */}
      {!isCompact && (
        <div className="flex flex-nowrap items-center gap-1.5">
          {TYPE_GROUPS.map((group, gi) => (
            <ToggleGroup key={gi} value={mode} onChange={handleMode}>
              {group.map(item => (
                <ToggleGroupItem key={item.value} value={item.value}>
                  {t(item.tKey)}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          ))}
        </div>
      )}

      {isCompact && (
        <>
          <Button
            ref={addRef}
            type="button"
            variant={mode?.startsWith('create_') ? 'default' : 'outline'}
            size="sm"
            onClick={() => setAddMenuOpen(o => !o)}
            aria-haspopup="menu"
            aria-expanded={addMenuOpen}
          >
            <Plus className="size-4" />
            <span>{t('zoning.toolbar.add')}</span>
          </Button>
          <Popover open={addMenuOpen} onOpenChange={setAddMenuOpen} triggerRef={addRef} side="bottom" align="start">
            <div className="flex flex-col py-1 min-w-[12rem]" role="menu">
              {TYPE_GROUPS.flat().map(item => (
                <button
                  key={item.value}
                  role="menuitem"
                  type="button"
                  onClick={() => handlePickFromAddMenu(item.value)}
                  className={cn(
                    'flex items-center px-3 py-1.5 text-sm text-left transition-colors hover:bg-muted hover:text-foreground',
                    mode === item.value && 'bg-primary-muted text-primary-muted-foreground'
                  )}
                >
                  {t(item.tKey)}
                </button>
              ))}
            </div>
          </Popover>
        </>
      )}

      {/* Right-side helpers */}
      <div className="ml-auto flex items-center gap-1.5">
        <Tooltip content={t('zoning.toolbar.hints')}>
          <Button
            ref={helpRef}
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => setHelpOpen(o => !o)}
            aria-label={t('zoning.toolbar.hints')}
            aria-haspopup="dialog"
            aria-expanded={helpOpen}
          >
            <HelpCircle className="size-4 text-muted-foreground" />
          </Button>
        </Tooltip>
        <Popover open={helpOpen} onOpenChange={setHelpOpen} triggerRef={helpRef} side="bottom" align="end">
          <div className="w-[22rem] max-w-[90vw] divide-y divide-border">
            {HINTS.map(group => (
              <section key={group.section} className="px-4 py-3">
                <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  {group.section}
                </div>
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs">
                  {group.items.map(([key, desc], i) => (
                    <React.Fragment key={i}>
                      <dt className="inline-flex items-center justify-self-start rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[11px] font-medium text-foreground whitespace-nowrap">
                        {key}
                      </dt>
                      <dd className="self-center text-muted-foreground leading-snug">{desc}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </section>
            ))}
          </div>
        </Popover>

        {onReloadFrame && (
          <Tooltip content={t('zoning.toolbar.reload')}>
            <Button
              type="button"
              variant="outline"
              size="icon-sm"
              onClick={onReloadFrame}
              disabled={!canReload || reloading}
              aria-label={t('zoning.toolbar.reload')}
            >
              {reloading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <span className="relative inline-flex items-center justify-center">
                  <ImageIcon className="size-4" />
                  <RotateCcw className="absolute -top-1 -right-1 size-2.5 rounded-full bg-card p-px text-foreground" />
                </span>
              )}
            </Button>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default PolygonToolbar;
